import React, { ChangeEvent } from 'react';
import { Stack } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import 'dayjs/locale/es';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { FormikTouched } from 'formik/dist/types';
import {
    DateTimeValidationError,
    PickerChangeHandlerContext,
} from '@mui/x-date-pickers/models';
import { useAuthContext } from '../../../../contexts/authContext';

type Props = {
    label: string;
    name: string;
    size?: undefined | 'small' | 'medium';
    margin?: 'dense' | 'normal' | 'none';
    required?: false | boolean;
    value?: any;
    touched?: boolean | FormikTouched<any> | FormikTouched<any>[] | undefined;
    errors?: string;
    minDate?: any;
    maxDate?: any;
    minTime?: any;
    maxTime?: any;
    disabled?: boolean;
    disablePast?: boolean;
    disableFuture?: boolean;
    onChange: (
        value: ChangeEvent<HTMLInputElement> | null,
        context: PickerChangeHandlerContext<DateTimeValidationError>
    ) => void;
};

const DateTimePickerField = ({
    label,
    name,
    touched,
    errors,
    minDate = undefined,
    maxDate = undefined,
    minTime = undefined,
    maxTime = undefined,
    disabled,
    disablePast = false,
    disableFuture = false,
    onChange,
    required,
    size,
    value,
}: Props) => {
    //onChange Formik
    //The variable DateTimeVariableFormik must be equal to the variable name of the formik
    //onChange={(value) => formik.setFieldValue('dateTimeVariableFormik', value, true)}

    const { modeTheme } = useAuthContext();

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                <Stack component="div">
                    <DateTimePicker
                        label={label}
                        value={value || null}
                        disabled={disabled}
                        disablePast={disablePast}
                        minDate={minDate}
                        maxDate={maxDate}
                        disableFuture={disableFuture}
                        ampm={true}
                        minTime={minTime}
                        maxTime={maxTime}
                        onChange={onChange}
                        slotProps={{
                            textField: {
                                name: name,
                                size: size,
                                required: required,
                                error: touched && Boolean(errors),
                                helperText: touched ? errors : '',
                            },
                        }}
                        viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                        }}
                        sx={{
                            '& label.Mui-focused': {
                                color:
                                    modeTheme === 'darkMode'
                                        ? 'primary.contrastText'
                                        : 'primary.main',
                            },
                            '& .MuiInput-underline:after': {
                                borderBottomColor:
                                    modeTheme === 'darkMode'
                                        ? 'primary.contrastText'
                                        : 'primary.main',
                            },
                            '& .MuiFilledInput-underline:after': {
                                borderBottomColor:
                                    modeTheme === 'darkMode'
                                        ? 'primary.contrastText'
                                        : 'primary.main',
                            },
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor:
                                        modeTheme === 'darkMode'
                                            ? 'primary.contrastText'
                                            : 'primary.main',
                                },
                            },
                        }}
                    />
                </Stack>
            </LocalizationProvider>
        </>
    );
};

export default DateTimePickerField;
